import React from "react";
import { TreeInstance, ItemInstance } from "@headless-tree/core";
import {
  FolderIcon,
  FolderOpenIcon,
  FileIcon,
  FileCsv as FileCsvIcon,
  EnvelopeSimpleIcon,
  SortAscending,
  SortDescending,
  CheckSquare,
  Square,
} from "@phosphor-icons/react";
import LockedFileIndicator from "@/components/common/LockedFileIndicator";
import { useFrameWidth } from "@/hooks/useFrameWidth";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";
import { formatLegacyDateString } from "@/utils/temporal";
import { Datum } from "./datastructure";
import type { RenderableData } from "./fetchFiles";
import { useFiletreeContextMenu, ContextMenuAction } from "./contextmenu";

import { Tooltip } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useRowPathTooltip, PathTooltipPopper } from "./PathTooltip";

// File type and sync folder icons
import OnenoteIcon from "../../assets/Icons/onenote.svg";
import ExcelIcon from "../../assets/Icons/excel.svg";
import WordIcon from "../../assets/Icons/docx.svg";
import PdfIcon from "../../assets/Icons/pdf.svg";
import PptxIcon from "../../assets/Icons/pptx.svg";
import EvestmentIcon from "../../assets/Icons/evestment.svg";
import AltivaIcon from "../../assets/Icons/altiva.svg";
import ImageFileIcon from "../../assets/Icons/image.svg";
import VideoFileIcon from "../../assets/Icons/video.svg";
import AudioFileIcon from "../../assets/Icons/audio.svg";
import TextFileIcon from "../../assets/Icons/text.svg";
import FpIcon from "../../assets/favicon.png";
import MicrosoftTeamsIcon from "../../assets/Icons/microsoft-teams.svg";
import MsSharepointIcon from "../../assets/Icons/ms-sharepoint.svg";
import BoxIcon from "../../assets/Icons/box.svg";
import DropboxIcon from "../../assets/Icons/dropbox.svg";
import GoogleDriveIcon from "../../assets/Icons/google-drive.svg";
import ReadOnlyDriveFolderIcon from "../../assets/Icons/folder.svg";
import { driveConnectorIcon } from "@/hooks/useDrivePolicy";

import {
  getSpecialFolderType,
  isReadOnlyDriveFolder,
  ReadOnlyContext,
  SpecialFolderType,
} from "./specialFolders";
import {
  imageExtensions as imageExts,
  videoExtensions as videoExts,
  audioExtensions as audioExts,
  textExtensions as textExts,
} from "@/components/common/FileTree/fileTypeExtensions";

interface FiletreeRendererProps {
  tree: TreeInstance<Datum>;
  isItemLoading: (itemId: string) => boolean;
  onItemClick?: (itemId: string) => void;
  onContextMenuAction?: (action: string, itemId: string) => void;
  getContextMenuActions?: (itemId: string, item: any) => ContextMenuAction[];
  className?: string;
  // Checkbox selection props
  showCheckboxes?: boolean;
  isItemSelected?: (itemId: string) => boolean;
  onCheckboxToggle?: (itemId: string) => void;
  /**
   * Whether still-processing (greyed-out) files may be selected. On the Documents
   * page (#10770) they can be, for bulk move/delete/open; in the query/report
   * modals they can't (#9880). Greying is unaffected either way. Defaults to false.
   */
  allowProcessingFileSelection?: boolean;
  // Pending move state (for optimistic update styling)
  isPendingMove?: (datum: Datum) => boolean;
  // Cut state (for visual dimming of cut items)
  isCut?: (datum: Datum) => boolean;
  // Sort props
  sortBy: "name" | "date";
  sortDirection: "asc" | "desc";
  onSortChange: (column: "name" | "date") => void;
  // Drive name lookup for tooltips
  driveIdToName?: Map<string, string>;
  // Scopes read-only client drives (served per-org, #10972) for icon/tooltip.
  readOnlyCtx?: ReadOnlyContext;
  /**
   * What the date column does when the tree's own frame is narrow (#11742,
   * CD (b)/(c)): "compact" (default) keeps it in numeric form; "hidden"
   * stands it down entirely — the selection modals choose "hidden" so the
   * name keeps the room. Wide frames always render the full date.
   */
  narrowDate?: "compact" | "hidden";
}

interface FileTreeItemProps {
  item: ItemInstance<Datum>;
  datum: Datum;
  depth: number;
  isExpanded: boolean;
  isLoading: boolean;
  isFolder: boolean;
  isDragTarget: boolean;
  isPendingMove: boolean;
  isCut: boolean;
  // Stable id-keyed callbacks so React.memo can short-circuit re-renders.
  onRowClick: (itemId: string, e: React.MouseEvent) => void;
  onContextMenu: (itemId: string, e: React.MouseEvent) => void;
  isContextMenuTarget: boolean;
  // Checkbox props
  showCheckbox: boolean;
  isSelected: boolean;
  onCheckboxToggle: (itemId: string) => void;
  // Whether still-processing files may be selected (Documents page: yes).
  allowProcessingFileSelection: boolean;
  // Drive name lookup for tooltips
  driveIdToName?: Map<string, string>;
  // Scopes read-only client drives (served per-org, #10972) for icon/tooltip.
  readOnlyCtx?: ReadOnlyContext;
  // Narrow-frame treatment of the row dates, mirrored from the renderer so
  // the header column tracks the cells (#11742).
  narrow?: boolean;
  narrowDate?: "compact" | "hidden";
}

interface TableHeaderProps {
  sortBy: "name" | "date";
  sortDirection: "asc" | "desc";
  onSortChange: (column: "name" | "date") => void;
  narrow?: boolean;
  narrowDate?: "compact" | "hidden";
}

export const TableHeader = ({
  sortBy,
  sortDirection,
  onSortChange,
  narrow = false,
  narrowDate = "compact",
}: TableHeaderProps) => (
  <div className="w-full border-b border-gray-200 bg-gray-50">
    <div className="flex items-center h-10 px-2 text-sm font-medium text-gray-700">
      <div
        className="flex-1 min-w-0 flex items-center gap-1 cursor-pointer hover:text-gray-900"
        onClick={() => onSortChange("name")}
      >
        <span>Name</span>
        {sortBy === "name" &&
          (sortDirection === "asc" ? (
            <SortAscending className="w-4 h-4" />
          ) : (
            <SortDescending className="w-4 h-4" />
          ))}
      </div>
      {!(narrow && narrowDate === "hidden") && (
        <div
          className={`${narrow ? "w-24" : "w-32"} flex-shrink-0 text-right flex items-center justify-end gap-1 cursor-pointer hover:text-gray-900 pr-2`}
          onClick={() => onSortChange("date")}
        >
          <span>Date</span>
          {sortBy === "date" &&
            (sortDirection === "asc" ? (
              <SortAscending className="w-4 h-4" />
            ) : (
              <SortDescending className="w-4 h-4" />
            ))}
        </div>
      )}
    </div>
  </div>
);

export function FiletreeRenderer({
  tree,
  isItemLoading,
  onItemClick,
  onContextMenuAction,
  getContextMenuActions,
  className = "",
  showCheckboxes = false,
  isItemSelected,
  onCheckboxToggle,
  allowProcessingFileSelection = false,
  isPendingMove,
  isCut,
  sortBy,
  sortDirection,
  onSortChange,
  driveIdToName,
  readOnlyCtx,
  narrowDate = "compact",
}: FiletreeRendererProps) {
  const { contextMenu, handleContextMenu, ContextMenuComponent } =
    useFiletreeContextMenu({
      tree,
      getMenuActions: getContextMenuActions,
      onMenuAction: onContextMenuAction,
    });

  // House rule (#11426/#11742): the tree keys on its own frame, never the
  // viewport — it lives in dialogs, cards and panes whose widths move
  // independently. A narrow frame drops the 400px floor (which ancestors
  // with overflow-hidden were CLIPPING, not scrolling), ellipsizes names and
  // narrows the date; until the first measurement lands the wide layout is
  // the fallback.
  const { frameRef, frameWidth } = useFrameWidth<HTMLDivElement>();
  const narrow = frameWidth !== null && frameWidth < 420;

  // Stable id-keyed callbacks so the memoized FileTreeItem only re-renders
  // when its own state changes (selection, drag, hover) instead of on every
  // parent re-render.
  const handleRowClickById = React.useCallback(
    (itemId: string, e: React.MouseEvent) => {
      const item = tree.getItemInstance(itemId);
      const isFolder = item.isFolder();
      const isLoading = isItemLoading(itemId);
      // Still-processing and locked files can't be queried against. On the
      // Documents page they're still selectable for bulk move/delete/open
      // (#10770); in the query/report modals selection stays blocked (#9880).
      // A failed file is neither: the agent reads its bytes (#13887).
      const payload = item.getItemData().payload;
      const selectionBlocked =
        isSelectionBlockedFile(payload) && !allowProcessingFileSelection;

      // Ctrl+Click (or Cmd+Click on Mac) toggles selection regardless of checkbox mode
      if (e.ctrlKey || e.metaKey) {
        if (!selectionBlocked) onCheckboxToggle?.(itemId);
        return;
      }

      // If checkboxes are shown, clicking the row shouldn't do selection
      // (that's handled by the checkbox). Just handle folder expansion.
      if (showCheckboxes) {
        if (isFolder && !isLoading) {
          item.isExpanded() ? item.collapse() : item.expand();
        } else if (!isFolder && !selectionBlocked) {
          onCheckboxToggle?.(itemId);
        }
        return;
      }

      // Regular click behavior when checkboxes aren't shown
      if (isFolder && !isLoading) {
        item.isExpanded() ? item.collapse() : item.expand();
      }

      onItemClick?.(itemId);
    },
    [
      tree,
      isItemLoading,
      showCheckboxes,
      onCheckboxToggle,
      onItemClick,
      allowProcessingFileSelection,
    ]
  );

  const handleContextMenuById = React.useCallback(
    (itemId: string, e: React.MouseEvent) => {
      handleContextMenu(e, itemId);
    },
    [handleContextMenu]
  );

  const handleCheckboxToggleById = React.useCallback(
    (itemId: string) => {
      onCheckboxToggle?.(itemId);
    },
    [onCheckboxToggle]
  );

  // Flat list of visible items, in DFS order, respecting which folders are
  // expanded. Mirrors the recursion in the prior non-virtualized code so
  // semantics are identical; the virtualizer then renders only the slice
  // intersecting the scroll viewport.
  const visibleItems = flattenVisibleItems(tree.getRootItem());

  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: visibleItems.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT_PX,
    // Render this many rows beyond the viewport on each side so fast scroll
    // doesn't surface blank space before the next paint.
    overscan: 40,
    getItemKey: (index) => visibleItems[index].item.getId(),
  });

  const rootItem = tree.getRootItem();
  const rootId = rootItem.getId();

  // Get drag-drop props from the root item so the empty space acts as a root drop target
  const rootProps = rootItem.getProps();
  const rootIsDragTarget =
    typeof rootItem.isDragTarget === "function"
      ? rootItem.isDragTarget()
      : false;

  return (
    // The measuring wrapper carries no min-width of its own — the observer
    // must read the FRAME's width, which the min-width on the tree body would
    // otherwise mask (a 350px frame holding a 400px-min tree measures 400).
    <div ref={frameRef} className="w-full h-full min-w-0">
      <div
        className={`${narrow ? "min-w-0" : "min-w-[400px]"} w-full h-full p-4 text-[14px] flex flex-col ${className}`}
      >
        <TableHeader
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
          narrow={narrow}
          narrowDate={narrowDate}
        />
        <div
          ref={scrollRef}
          data-filetree-scroll
          className="w-full border border-gray-100 overflow-auto flex-1 min-h-0 flex flex-col"
        >
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              position: "relative",
              width: "100%",
              flexShrink: 0,
            }}
          >
            {virtualizer.getVirtualItems().map((vRow) => {
              const { item, depth } = visibleItems[vRow.index];
              const itemId = item.getId();
              const datum = item.getItemData();
              const isFolder = item.isFolder();
              const isDragTarget =
                typeof item.isDragTarget === "function"
                  ? item.isDragTarget()
                  : false;
              return (
                <div
                  key={itemId}
                  ref={virtualizer.measureElement}
                  data-index={vRow.index}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${vRow.start}px)`,
                  }}
                >
                  <FileTreeItem
                    item={item}
                    datum={datum}
                    depth={depth}
                    isExpanded={item.isExpanded()}
                    isLoading={isItemLoading(itemId)}
                    isFolder={isFolder}
                    isDragTarget={isDragTarget}
                    isPendingMove={isPendingMove?.(datum) ?? false}
                    isCut={isCut?.(datum) ?? false}
                    onRowClick={handleRowClickById}
                    onContextMenu={handleContextMenuById}
                    isContextMenuTarget={
                      contextMenu.visible && contextMenu.itemId === itemId
                    }
                    showCheckbox={showCheckboxes}
                    isSelected={isItemSelected?.(itemId) ?? false}
                    onCheckboxToggle={handleCheckboxToggleById}
                    allowProcessingFileSelection={allowProcessingFileSelection}
                    driveIdToName={driveIdToName}
                    readOnlyCtx={readOnlyCtx}
                    narrow={narrow}
                    narrowDate={narrowDate}
                  />
                </div>
              );
            })}
          </div>
          {/* Root-level drop zone and context menu target (empty space below items) */}
          <div
            ref={rootProps.ref}
            onDragOver={rootProps.onDragOver}
            onDragEnter={rootProps.onDragEnter}
            onDragLeave={rootProps.onDragLeave}
            onDrop={rootProps.onDrop}
            onContextMenu={(e) => handleContextMenu(e, rootId)}
            className={`flex-1 min-h-[60px] ${rootIsDragTarget ? "bg-blue-50 outline outline-2 outline-blue-400" : ""}`}
          />
        </div>
        {ContextMenuComponent}
      </div>
    </div>
  );
}

// Initial estimate; the virtualizer's measureElement refines per-row as
// each row mounts. Rows are roughly text-line height plus py-1 padding.
const ROW_HEIGHT_PX = 28;

interface VisibleItem {
  item: ItemInstance<Datum>;
  depth: number;
}

// DFS walk of the tree, emitting one entry per visible (expanded) item.
// Root itself is skipped — its direct children render at depth 0, matching
// the prior recursive renderer's behavior.
function flattenVisibleItems(rootItem: ItemInstance<Datum>): VisibleItem[] {
  const out: VisibleItem[] = [];
  const walk = (item: ItemInstance<Datum>, depth: number) => {
    out.push({ item, depth });
    if (item.isExpanded() && item.isFolder()) {
      for (const child of item.getChildren()) {
        walk(child, depth + 1);
      }
    }
  };
  for (const child of rootItem.getChildren()) {
    walk(child, 0);
  }
  return out;
}

const FileTreeItem = React.memo(function FileTreeItem({
  item,
  datum,
  depth,
  isExpanded,
  isLoading,
  isFolder,
  isDragTarget,
  isPendingMove,
  isCut,
  onRowClick,
  onContextMenu,
  isContextMenuTarget,
  showCheckbox,
  isSelected,
  onCheckboxToggle,
  allowProcessingFileSelection,
  driveIdToName,
  readOnlyCtx,
  narrow = false,
  narrowDate = "compact",
}: FileTreeItemProps) {
  const itemId = item.getId();
  const { payload } = datum;
  const rowRef = React.useRef<HTMLDivElement | null>(null);
  // Per-row hover state — keeps the tooltip mount local so hovering a row
  // only re-renders that one row, never the parent or peer rows.
  const { show: tooltipOpen, bind: hoverBind } = useRowPathTooltip();
  // Use driveName from payload for synthetic drive folders, otherwise use path-derived name
  const itemName =
    (payload?.type === "folder" && payload.driveName) ||
    datum.name ||
    "Root Datum";
  // Selectability only; greying is isGreyedFile below.
  const selectionBlocked =
    isSelectionBlockedFile(payload) && !allowProcessingFileSelection;
  const viewerZone = useViewerTimeZone();
  const formattedDate = payload
    ? formatDate(
        getDisplayDate(payload) || "",
        viewerZone,
        narrow ? "numeric" : "medium"
      )
    : "--";

  // A locked file grays like any processing file, with the lock mark saying
  // why; both leave the moment its password is accepted (#11856).
  const isLocked = isLockedFile(payload);
  const greyed = isGreyedFile(payload);
  const dimmed = greyed || isPendingMove;
  const isChecked = showCheckbox && isSelected;
  const textStyle = isLoading
    ? "text-gray-500"
    : dimmed
      ? "text-gray-400"
      : isCut
        ? "text-gray-400 italic"
        : isChecked
          ? "text-[#2A3DCB]"
          : "";

  // Build row styles with selection and drag-target states
  const rowStyles = [
    "cursor-pointer",
    "select-none",
    dimmed ? "opacity-50" : "opacity-100",
    isCut ? "bg-amber-50/60" : "",
    isContextMenuTarget ? "bg-gray-200" : "",
    isChecked ? "bg-[#F7F8FF]" : !isCut ? "hover:bg-gray-100" : "",
    isDragTarget ? "bg-blue-50 outline outline-2 outline-blue-400" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Get props from headless-tree (includes drag-drop event handlers when feature is enabled)
  const itemProps = item.getProps();

  // Grid columns: checkbox (optional) + icon + name
  const gridColumns = showCheckbox ? "16px 16px 1fr" : "16px 1fr";

  // Merge headless-tree's ref (if any) with our own so we can anchor the
  // tooltip Popper to this row.
  const headlessRef = itemProps.ref as
    | React.RefCallback<HTMLDivElement>
    | React.MutableRefObject<HTMLDivElement | null>
    | undefined;
  const setRowRef = (el: HTMLDivElement | null) => {
    rowRef.current = el;
    if (typeof headlessRef === "function") {
      headlessRef(el);
    } else if (headlessRef) {
      headlessRef.current = el;
    }
  };

  return (
    <>
      <div
        {...itemProps}
        ref={setRowRef}
        className={`flex items-center ${rowStyles}`}
        onClick={(e) => onRowClick(itemId, e)}
        onContextMenu={(e) => onContextMenu(itemId, e)}
        onMouseEnter={hoverBind.onMouseEnter}
        onMouseLeave={hoverBind.onMouseLeave}
      >
        <div
          className="py-1 px-2 flex-1 min-w-0"
          style={{ paddingLeft: `${depth * 20 + 8}px` }}
        >
          <div
            className="grid gap-2 items-center"
            style={{ gridTemplateColumns: gridColumns }}
          >
            {showCheckbox && (
              <div
                role="checkbox"
                aria-checked={isSelected}
                aria-disabled={selectionBlocked}
                aria-label={`Select ${itemName}`}
                onClick={(e) => {
                  e.stopPropagation();
                  // In the query/report modals a still-processing file isn't
                  // selectable (nothing to query against yet); on the Documents
                  // page it is (#10770). Greying is unchanged either way.
                  if (selectionBlocked) return;
                  onCheckboxToggle(itemId);
                }}
                className={`${selectionBlocked ? "cursor-default" : "cursor-pointer"} flex items-center justify-center`}
              >
                {isSelected ? (
                  <CheckSquare
                    size={16}
                    weight="fill"
                    style={{ color: "#2A3DCB" }}
                  />
                ) : (
                  <Square size={16} className="text-gray-300" />
                )}
              </div>
            )}
            <div className="w-4 h-4 flex items-center justify-center">
              {getItemIcon(
                datum,
                isExpanded,
                isLoading,
                greyed,
                isFolder,
                readOnlyCtx
              )}
            </div>
            <div
              className={`flex items-center gap-1.5 ${narrow ? "min-w-0" : ""}`}
            >
              {/* Narrow frames ellipsize the name inside the row; wide frames
                  keep the historical h-scroll behavior (#11742). */}
              <span
                className={`${textStyle} ${narrow ? "truncate min-w-0" : ""}`}
              >
                {itemName}
              </span>
              {isLocked && payload?.type === "file" && (
                <LockedFileIndicator status={payload.status} />
              )}
            </div>
          </div>
        </div>
        {!(narrow && narrowDate === "hidden") && (
          <div
            className={`py-1 px-2 text-right text-sm text-gray-500 ${narrow ? "w-24" : "w-32"} flex-shrink-0`}
          >
            <span className={greyed ? "text-gray-400" : ""}>
              {formattedDate}
            </span>
          </div>
        )}
      </div>
      {tooltipOpen && (
        <PathTooltipPopper
          anchor={rowRef.current}
          datum={datum}
          isFolder={isFolder}
          driveIdToName={driveIdToName}
        />
      )}
    </>
  );
});

// Helper functions
// TODO: Move this logic to the backend
export const getDisplayDate = (payload: RenderableData): string | null => {
  if (payload.type === "file") {
    if (payload.documentDate) {
      const documentDate = new Date(payload.documentDate);
      if (documentDate > new Date() && payload.uploadedAt)
        return payload.uploadedAt;
      return payload.documentDate;
    }
    return payload.uploadedAt || null;
  }
  return payload.documentDate || null;
};

// The date shown on a file-tree row. `viewerZone` is the zone an instant --
// an upload time -- should be read in; a document date is a calendar date and
// ignores it entirely.
//
// Both file trees call this, through the same shared module, which is what
// stops them disagreeing about the same value the way they did in #11055: this
// one forced UTC while the other read the viewer's local zone, so a document
// dated the 28th rendered as the 28th here and the 27th there.
export const formatDate = (
  dateString: string,
  viewerZone: string,
  format: "medium" | "numeric" = "medium"
): string => formatLegacyDateString(dateString, viewerZone, format) ?? "--";

/** Whether Finpilot's processing of a file failed (its newest task is
 *  `failed`). Such a file is not coming any further, so it is not
 *  "processing": it can be picked and attached, and the agent reads its
 *  stored bytes (#13887). */
export const isFailedFile = (payload: RenderableData): boolean =>
  payload.type === "file" && payload.status === "failed";

// TODO: Move this logic to the backend
/** Whether a file is still being processed: its task has not finished.
 *  A failed file is not (#13887). */
export const isProcessingFile = (payload: RenderableData): boolean =>
  payload.type === "file" &&
  !["completed", "partially-completed", "failed"].includes(
    payload.status || ""
  );

/** Whether a row carries the lock mark: flagged as password-protected,
 *  without a stored password, and never processed (a processed file with the
 *  flag still set is the legacy shape, and `LockedFileIndicator` hides on
 *  that status too). The mark and the gray leave together. */
export const isLockedFile = (payload: RenderableData | undefined): boolean =>
  !!payload &&
  payload.type === "file" &&
  !!payload.isPasswordProtected &&
  !payload.hasStoredPassword &&
  !["completed", "partially-completed"].includes(payload.status || "");

/** Whether a row is grayed. A file still processing is, since there is
 *  nothing to open yet — except one whose password is stored: that file is
 *  unlocked and opens, so it reads plain from the moment the password is
 *  accepted, while its task re-runs and after (#11856, Hunter's ruling). A
 *  locked file is grayed whatever its task status: its failure ("needs a
 *  password") is what the lock shows (#13887). */
export const isGreyedFile = (payload: RenderableData | undefined): boolean =>
  !!payload &&
  ((isProcessingFile(payload) &&
    !(payload.type === "file" && payload.hasStoredPassword)) ||
    isLockedFile(payload));

/** Whether a row refuses selection where processing files are blocked: a
 *  file still processing, or a locked one (#13887). A failed file does not. */
export const isSelectionBlockedFile = (
  payload: RenderableData | undefined
): boolean => !!payload && (isProcessingFile(payload) || isLockedFile(payload));

/**
 * Get the special folder type for icon display.
 * Uses the centralized specialFolders utility.
 */
const getSyncType = (
  datum: Datum,
  ctx?: ReadOnlyContext
): SpecialFolderType => {
  return getSpecialFolderType(datum.path, ctx);
};

/**
 * Get file icon for a RenderableData payload (files only, not folders).
 * Exported for use in SearchResultsView.
 */

// Hoisted to module scope so the row render path does not re-allocate
// these tables on every paint of every visible row.
const FILE_TYPE_ICONS: Record<string, string> = {
  pdf: PdfIcon,
  "onenote-page": OnenoteIcon,
  excel: ExcelIcon,
  docx: WordIcon,
  pptx: PptxIcon,
  "evestment-note": EvestmentIcon,
  "finpilot-interaction": FpIcon,
  altvia_interaction: AltivaIcon,
  altvia_interaction_note: AltivaIcon,
};

const SYNC_FOLDER_ICONS: Record<string, string> = {
  "ms-teams": MicrosoftTeamsIcon,
  "ms-sharepoint": MsSharepointIcon,
  "ms-onenote": OnenoteIcon,
  box: BoxIcon,
  "google-drive": GoogleDriveIcon,
  dropbox: DropboxIcon,
};

export const getFileIcon = (
  payload: RenderableData,
  greyed: boolean
): React.ReactNode => {
  const iconClass = greyed ? "text-gray-400" : "text-gray-600";
  const imgClass = `w-[14px] min-w-[14px] ${greyed ? "opacity-40" : ""}`;

  if (!payload || payload.type !== "file") {
    return <FileIcon size={14} className={iconClass} />;
  }

  const fileIcon = FILE_TYPE_ICONS[payload.fileType];
  if (fileIcon) {
    const className =
      payload.fileType === "docx"
        ? `w-[18px] min-w-[18px] ${greyed ? "opacity-40" : ""}`
        : imgClass;
    return (
      <img
        src={fileIcon}
        className={className}
        alt={`${payload.fileType} file`}
      />
    );
  }

  if (payload.fileType === "csv") {
    return <FileCsvIcon size={14} className={iconClass} />;
  }

  if (payload.fileType === "email") {
    return <EnvelopeSimpleIcon size={14} className={iconClass} />;
  }

  if (payload.fileType === "image") {
    return <img src={ImageFileIcon} className={imgClass} alt="image file" />;
  }

  if (payload.fileType === "unsupported") {
    const ext = (payload.fileName || "").split(".").pop()?.toLowerCase() ?? "";
    if (imageExts.has(ext))
      return <img src={ImageFileIcon} className={imgClass} alt="image file" />;
    if (videoExts.has(ext))
      return <img src={VideoFileIcon} className={imgClass} alt="video file" />;
    if (audioExts.has(ext))
      return <img src={AudioFileIcon} className={imgClass} alt="audio file" />;
    if (textExts.has(ext))
      return <img src={TextFileIcon} className={imgClass} alt="text file" />;
  }

  return <FileIcon size={14} className={iconClass} />;
};

const getItemIcon = (
  datum: Datum,
  isExpanded: boolean,
  isLoading: boolean,
  greyed: boolean,
  isFolder: boolean,
  readOnlyCtx?: ReadOnlyContext
) => {
  const iconClass = greyed ? "text-gray-400" : "text-gray-600";
  const imgClass = `w-[14px] min-w-[14px] ${greyed ? "opacity-40" : ""}`;

  if (isLoading) {
    return (
      <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full" />
    );
  }

  if (isFolder) {
    // Check for a read-only client drive first (special read-only folder with custom icon)
    if (isReadOnlyDriveFolder(datum, readOnlyCtx)) {
      const driveName = datum.name ?? "";
      return (
        <Tooltip title={`${driveName} (Read-only)`}>
          <span className="inline-flex">
            <img
              src={
                driveConnectorIcon(driveName, readOnlyCtx?.driveConnectors) ??
                ReadOnlyDriveFolderIcon
              }
              className={imgClass}
              alt={`${driveName} folder`}
            />
          </span>
        </Tooltip>
      );
    }

    const syncType = getSyncType(datum, readOnlyCtx);
    if (syncType && SYNC_FOLDER_ICONS[syncType]) {
      return (
        <img
          src={SYNC_FOLDER_ICONS[syncType]}
          className={imgClass}
          alt={`${syncType} folder`}
        />
      );
    }

    return isExpanded ? (
      <FolderOpenIcon size={16} weight="regular" className={iconClass} />
    ) : (
      <FolderIcon size={16} weight="regular" className={iconClass} />
    );
  }

  // File icons - delegate to shared function
  return datum.payload ? (
    getFileIcon(datum.payload, greyed)
  ) : (
    <FileIcon size={14} className={iconClass} />
  );
};
